import { normalize360 } from './ayanamsa.ts';

/** The twelve rashis, in order from sidereal 0°. */
export const RASHIS = [
  'Mesha', 'Vrishabha', 'Mithuna', 'Karka', 'Simha', 'Kanya',
  'Tula', 'Vrishchika', 'Dhanu', 'Makara', 'Kumbha', 'Meena',
] as const;

export const RASHI_ENGLISH = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
] as const;

/** The twenty-seven nakshatras, in order from sidereal 0°. */
export const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
  'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati',
] as const;

export type Rashi = (typeof RASHIS)[number];
export type Nakshatra = (typeof NAKSHATRAS)[number];

export const RASHI_ARC = 30;
export const NAKSHATRA_ARC = 360 / 27; // 13°20'
export const PADA_ARC = NAKSHATRA_ARC / 4; // 3°20'

export interface Placement {
  longitude: number;
  rashiIndex: number;
  rashi: Rashi;
  rashiEnglish: string;
  /** Degrees within the rashi, 0–30. */
  degreeInRashi: number;
  nakshatraIndex: number;
  nakshatra: Nakshatra;
  /** Pada 1–4 within the nakshatra. */
  pada: number;
}

export function rashiIndexOf(longitude: number): number {
  return Math.floor(normalize360(longitude) / RASHI_ARC);
}

export function nakshatraIndexOf(longitude: number): number {
  return Math.floor(normalize360(longitude) / NAKSHATRA_ARC);
}

/** Pada number 1–4. */
export function padaOf(longitude: number): number {
  const within = normalize360(longitude) % NAKSHATRA_ARC;
  return Math.floor(within / PADA_ARC) + 1;
}

export function placementOf(longitude: number): Placement {
  const lon = normalize360(longitude);
  const ri = rashiIndexOf(lon);
  const ni = nakshatraIndexOf(lon);
  return {
    longitude: lon,
    rashiIndex: ri,
    rashi: RASHIS[ri]!,
    rashiEnglish: RASHI_ENGLISH[ri]!,
    degreeInRashi: lon - ri * RASHI_ARC,
    nakshatraIndex: ni,
    nakshatra: NAKSHATRAS[ni]!,
    pada: padaOf(lon),
  };
}

/**
 * Whole-sign houses — the standard Parashari system, in which the rashi
 * containing the Lagna is the whole of house 1.
 *
 * @returns house number 1–12
 */
export function wholeSignHouse(longitude: number, ascendant: number): number {
  const diff = rashiIndexOf(longitude) - rashiIndexOf(ascendant);
  return ((diff % 12) + 12) % 12 + 1;
}

/** Distance in degrees to the next boundary of the given division. */
export function distanceToNextBoundary(longitude: number, arc: number): number {
  const lon = normalize360(longitude);
  return arc - (lon % arc);
}

/** Distance in degrees back to the previous boundary of the given division. */
export function distanceToPrevBoundary(longitude: number, arc: number): number {
  return normalize360(longitude) % arc;
}

/** Formats a longitude as sign + degrees/minutes, e.g. "Simha 12°34'". */
export function formatPlacement(longitude: number): string {
  const p = placementOf(longitude);
  const deg = Math.floor(p.degreeInRashi);
  const min = Math.floor((p.degreeInRashi - deg) * 60);
  return `${p.rashi} ${deg}°${String(min).padStart(2, '0')}'`;
}
